import { Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import type { AdminTenant, AdminTenantListFilters } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { tenantStatusTone } from './domain';

interface TenantsListViewProps {
  filters: AdminTenantListFilters;
  onFiltersChange: (patch: Partial<AdminTenantListFilters>) => void;
}

export function TenantsListView({ filters, onFiltersChange }: TenantsListViewProps) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.listAdminTenants(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={8} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.tenants.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState title={translate('pay.tenants.list.emptyTitle')} body={translate('pay.tenants.list.emptyBody')} />
      </Card>
    );
  }

  const columns: Array<DataGridColumn<AdminTenant>> = [
    {
      key: 'code',
      label: translate('pay.tenants.list.columnCode'),
      mono: 'code',
      render: (item) => item.code,
    },
    {
      key: 'name',
      label: translate('pay.tenants.list.columnName'),
      render: (item) => item.name,
    },
    {
      key: 'status',
      mobileVisible: true,
      label: translate('pay.tenants.list.columnStatus'),
      render: (item) => (
        <StatusChip label={translate(`pay.tenants.status.${item.status}`)} tone={tenantStatusTone(item.status)} />
      ),
    },
    {
      key: 'createdAt',
      label: translate('pay.tenants.list.columnCreatedAt'),
      render: (item) => new Date(item.createdAt).toLocaleString('es-ES'),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={data.items}
      getRowKey={(item) => item.code}
      onRowClick={(item) => navigate(`/tenants/${encodeURIComponent(item.code)}`)}
      rowAriaLabel={translate('pay.tenants.list.viewDetail')}
      pagination={{
        page: data.page.page,
        pageSize: data.page.pageSize,
        totalItems: data.page.totalItems,
        rowsPerPageLabel: translate('pay.tenants.list.rowsPerPage'),
        onPageChange: (page) => onFiltersChange({ page }),
        onPageSizeChange: (pageSize) => onFiltersChange({ pageSize, page: 1 }),
      }}
    />
  );
}
